import { Box, Divider, Grid } from "@mui/material";
import { ReactComponent as ActionNumber } from "assets/ActionNumber.svg";
import { ReactComponent as DeleteIcon } from "assets/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "assets/EditIcon.svg";

import ThemeContext from "components/app/Context";
import { setPlanMode } from "components/features/CustomPlanCreation/CustomPlanCreation.slice";
import { fetchMyPlanDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import DeleteDialog from "components/features/MyPlans/Delete";
import Edit from "components/features/MyPlans/Edit";
import PlanAssignment from "components/features/PlanAssignment";
import MetricDetails from "components/features/dashboard/MetricDetails";
import InvalidPlanButton from "components/features/library/SuggestedPlans/InvalidPlanButton";
import { setisBookmarked } from "components/features/planDetails/PlanDetail.slice";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { changePendoByAction, getHoursAndMin, getPlanDetail } from "utils/CommonFunctions";
import { getPlanDetailsRoute, getPlansRoute, hasTemplatePlansAccess } from "utils/StoreUtils";
import { formTypes, planTypes } from "utils/Types";
import BookmarkPlan from "../Bookmark";
import Button from "../Button";
import FunctionalAreas from "../FunctionalAreas";
import InfoBar from "../InfoBar";
import KebabMenu from "../KebabMenu";
import PlanCard from "../PlanCard";
import PlanType from "../PlanType";
import TargetedPlanChip from "../TargetedPlanChip";
import Timing from "../Timing";
import Typography from "../Typography";
import "./item.scss";

interface Props {
  item: any;
  showBookmarked?: boolean;
  isBookmarked?: boolean;
  suggested?: boolean;
  redirectFromDashboard?: boolean;
  individualPropertyInncode?: any;
  fetchAllImplementedPlans?: boolean;
  isSuggestedPannel?: boolean;
}

const MyPlanBadge = (props: any) => {
  const { isMobileScreen } = useContext(ThemeContext);
  const { data } = props;

  if (data.myPlans && data.myPlans[0]?.title === data?.name) {
    return (
      <Typography
        variant={isMobileScreen ? "body1" : "label"}
        sx={{
          "&::before": {
            content: '""',
            display: "inline-block",
            height: "8px",
            width: "8px",
            borderRadius: "100%",
            backgroundColor: tokens.Primary.Primary400,
            marginRight: "4px"
          }
        }}
      >
        Plan Added
      </Typography>
    );
  }

  return null;
};

function Item(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const { isMobileScreen } = useContext(ThemeContext);

  const {
    item,
    showBookmarked = true,
    isBookmarked = false,
    suggested,
    redirectFromDashboard = false,
    fetchAllImplementedPlans = false,
    isSuggestedPannel = false
  } = props;
  const [openEditPlan, setOpenEditPlan] = useState(false);
  const [isOpenPlanAssignmentDialog, setIsOpenPlanAssignmentDialog] = useState(false);
  const [openDeleteCapthaDialog, setOpenDeleteCapthaDialog] = useState(Boolean);

  const { isConsultant } = useAppSelector((state) => state.app.loggedInUserDetails);
  const metricsPlansList = useAppSelector((state) => state.suggestedPlans.plansMetrics);

  const metrics = suggested && getPlanDetail(item?.id, metricsPlansList)?.metrics;

  const showMyPlansMenu = location.pathname.split("/")[1] === getPlansRoute().split("/")[1] && hasTemplatePlansAccess();
  const isTemplatePlan = searchParams.get("parentTab") === translate("common.templatePlans");

  const sharedSearch = searchParams.get("uuid") as string;

  const planMenu = [
    {
      label: "Assign Plan",
      enabled: true,
      action: async () => {
        setIsOpenPlanAssignmentDialog(true);
      }
    }
  ];
  const myPlanMenu = [
    {
      label: "Edit Plan",
      accessType: accessTypes.update,
      icon: EditIcon,
      enabled: true,
      action: async () => {
        await dispatch(fetchMyPlanDetailAsync({ id: item?.id }));
        if (isTemplatePlan) dispatch(setPlanMode(formTypes.CreateTemplatePlan));
        setOpenEditPlan(true);
        changePendoByAction(item.innCode);
      }
    },
    {
      label: "Delete Plan",
      icon: DeleteIcon,
      accessType: accessTypes.delete,
      enabled: true,
      action: () => {
        setOpenDeleteCapthaDialog(true);
        changePendoByAction(item.innCode);
      }
    }
  ];

  const onViewDetailsButtonClick = () => {
    dispatch(setisBookmarked(isBookmarked));
    const route = getPlanDetailsRoute();
    navigate(showMyPlansMenu ? `/${route}/${item?.id}` : `/plandetails/${item.id}`, {
      state: {
        innCode: params?.inncode,
        redirectFromSuggested: suggested,
        redirectFromDashboard: redirectFromDashboard,
        fetchAllImplementedPlans: fetchAllImplementedPlans,
        isSuggestedPannel: isSuggestedPannel,
        sharedSearch: sharedSearch
      }
    });
  };

  const getActionButtons = () => {
    return (
      <Grid display="flex" alignItems={"flex-start"} marginLeft={isMobileScreen ? "" : "auto"}>
        <Grid gap={"8px"} item display="flex" alignItems={"center"} width={"100%"}>
          {!isTemplatePlan && showBookmarked && (
            <>
              <BookmarkPlan view={"allPlan"} cmsId={item.id} isBookmarked={isBookmarked} />
              <Divider className="divider" orientation="vertical"></Divider>
            </>
          )}
          <Timing duration={getHoursAndMin(item?.timeEstimateMinutes)} />

          {showMyPlansMenu ? (
            <KebabMenu sx={{ marginLeft: "auto" }} options={myPlanMenu} />
          ) : (
            isConsultant && !redirectFromDashboard && <KebabMenu sx={{ marginLeft: "auto" }} options={planMenu} />
          )}
          {suggested && <InvalidPlanButton {...item} />}
        </Grid>
      </Grid>
    );
  };

  const getBottomPanel = () => {
    const infobar = (
      <InfoBar
        label="Contain"
        logo={<ActionNumber />}
        value={`${item?.numCards ?? item?.totalCards} Actions`}
      ></InfoBar>
    );

    const metrix =
      metrics &&
      Object.keys(metrics)?.map((metric: any) => {
        return <MetricDetails key={metric} metric={metrics[metric]} metricsBar={false} />;
      });

    const viewPlanDetailsButton = (
      <Button
        sx={{ marginLeft: "auto" }}
        variant="secondary"
        onClick={onViewDetailsButtonClick}
        label="View Plan Details"
      />
    );

    return isMobileScreen ? (
      <>
        {viewPlanDetailsButton}
        <Grid item xs={12}>
          {infobar}
        </Grid>
        <Grid item xs={12} display="flex" flexDirection="column" gap={1}>
          {metrix}
        </Grid>
      </>
    ) : (
      <Grid container>
        <Box width="80%" display="flex" flexWrap="wrap" gap={1}>
          {infobar}
          {metrix}
        </Box>
        {viewPlanDetailsButton}
      </Grid>
    );
  };

  const handleCloseDialog = () => {
    setIsOpenPlanAssignmentDialog(false);
  };

  const dialogs = () => {
    return (
      <>
        {isOpenPlanAssignmentDialog && (
          <PlanAssignment
            open={isOpenPlanAssignmentDialog}
            flow="third"
            plans={[item]}
            handleCloseDialog={handleCloseDialog}
            corpUserSelection={true}
            userSelectionFlow={"one"}
          />
        )}
        {openEditPlan && <Edit openEditPlan={openEditPlan} setOpenEditPlan={setOpenEditPlan} />}
        {openDeleteCapthaDialog && (
          <DeleteDialog
            openDeleteCapthaDialog={openDeleteCapthaDialog}
            setOpenDeleteCapthaDialog={setOpenDeleteCapthaDialog}
            plan={item}
          />
        )}
      </>
    );
  };

  return (
    <>
      <PlanCard type={planTypes.AllPlans}>
        <Box width={item?.isTargeted ? "calc(100% - 32px)" : "100%"} className="action-card_all-plans">
          <Grid container display={isMobileScreen ? "block" : "flex"}>
            <Grid item md={9} className="top-panel">
              {isMobileScreen && getActionButtons()}
              {(item?.planType || suggested || item?.isSuggestedPlan) && (
                <PlanType
                  isSuggestedSection={suggested}
                  isSuggested={item?.isSuggestedPlan}
                  planType={item?.planType}
                  brandDeadlineDate={item?.brandDeadlines?.[0]?.deadline}
                  initiativeName={item?.initiativeName}
                />
              )}
              <MyPlanBadge data={item} />
              <Typography variant={"h5"} color={tokens.Text.General.PrimaryHeadline} showBold>
                {item?.name ?? item?.title}
              </Typography>
              <FunctionalAreas functionalAreaIds={item?.functionalAreaIds} />
              {isMobileScreen && getBottomPanel()}
            </Grid>
            {!isMobileScreen && getActionButtons()}
          </Grid>
          {!isMobileScreen && getBottomPanel()}
        </Box>
        {item?.isTargeted && <TargetedPlanChip variant="vertical" />}
      </PlanCard>

      {dialogs()}
    </>
  );
}

export default Item;
